import { Helmet } from 'react-helmet'
import { REACT_APP_PUBLIC_URL } from 'env'
import { MetaTags } from 'components/MetaTags'
import { ANALYTICS_PATH } from 'paths'
import useURLParams from 'hooks/useURLParams'
import Styles from './Analytics.module.scss'
import InstagramAnalytics from './InstagramAnalytics/InstagramAnalytics'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { CheckBroken, Plus } from 'blaze-ui/icons'
import { useModals } from 'almanac-ui/Modal'
import { IntegrationsModal } from 'components/IntegrationsModal/IntegrationsModal'
import FacebookAnalytics from './FacebookAnalytics/FacebookAnalytics'
import LinkedInAnalytics from './LinkedInAnalytics/LinkedInAnalytics'
import XAnalytics from './XAnalytics/XAnalytics'
import { Empty, EmptyProps } from './components/Empty/Empty'
import { useInitiateOauth } from 'hooks/useInitiateOauth'
import { LoadingSkeleton } from './components/LoadingSkeleton/LoadingSkeleton'
import { AnalyticsProvider } from './AnalyticsContext'
import { Header } from './components/Header/Header'
import { IntegrationsBanner } from 'components/IntegrationsBanner/IntegrationsBanner'
import UserProfileAdd2 from 'blaze-ui/icons/20/UserProfileAdd2'
import { useOpenIntegrations } from 'hooks/useOpenIntegrations'

export const ANALYTICS_SUPPORTED_PROVIDERS = [
  ExternalIntegrationEnum.Instagram,
  ExternalIntegrationEnum.Facebook,
  ExternalIntegrationEnum.LinkedIn,
  ExternalIntegrationEnum.X,
] as const

type AnalyticsSupportedProvider = (typeof ANALYTICS_SUPPORTED_PROVIDERS)[number]

export const REQUIRED_SCOPES_BY_PROVIDER: Record<AnalyticsSupportedProvider, string[]> = {
  [ExternalIntegrationEnum.Instagram]: ['instagram_basic', 'instagram_manage_insights'],
  [ExternalIntegrationEnum.Facebook]: ['pages_read_engagement', 'read_insights'],
  [ExternalIntegrationEnum.LinkedIn]: ['rw_organization_admin'],
  [ExternalIntegrationEnum.X]: [],
}

const isSupportedProvider = (provider: ExternalIntegrationEnum): provider is AnalyticsSupportedProvider => {
  return ANALYTICS_SUPPORTED_PROVIDERS.includes(provider as never)
}

const Analytics = () => {
  const [searchParams, setSearchParams] = useURLParams()
  const { data, isLoading } = useExternalIntegrations()
  const { openModal } = useModals()
  const { initiateOauth } = useInitiateOauth()
  const selectedIntegrationId = searchParams.get('external_integration_id')
  const openIntegrations = useOpenIntegrations()

  const supportedIntegrations =
    data?.external_integrations.filter((integration) => isSupportedProvider(integration.provider)) || []

  const activeIntegration = supportedIntegrations.find(
    (integration) => integration.id.toString() === selectedIntegrationId,
  )

  if (!selectedIntegrationId && supportedIntegrations.length > 0) {
    setSearchParams({ external_integration_id: supportedIntegrations[0].id.toString() })
  }

  const hasValidIntegrations = supportedIntegrations.length > 0

  const getEmptyProps = (): EmptyProps | undefined => {
    if (isLoading) return
    if (!hasValidIntegrations) {
      return {
        text: 'Track your followers, profile interactions, post performance & much more.',
        buttonProps: {
          label: 'Connect Social Media',
          onPress: () => openIntegrations(),
          frontIcon: UserProfileAdd2,
        },
      }
    }

    if (
      activeIntegration &&
      activeIntegration.provider === ExternalIntegrationEnum.LinkedIn &&
      activeIntegration.account_type === 'profile'
    ) {
      return {
        text: 'LinkedIn analytics are currently only available for company pages.',
        buttonProps: {
          label: 'Connect a LinkedIn company page',
          onPress: () => openModal(IntegrationsModal, {}),
          frontIcon: Plus,
        },
      }
    }

    if (
      activeIntegration &&
      REQUIRED_SCOPES_BY_PROVIDER[activeIntegration.provider as AnalyticsSupportedProvider].some(
        (scope) => !activeIntegration.scopes.includes(scope),
      )
    ) {
      return {
        text: 'We need to request additional permissions to access your account analytics.',
        buttonProps: {
          label: 'Give us permission to access your stats',
          onPress: () => initiateOauth(activeIntegration.provider, activeIntegration.id),
          frontIcon: CheckBroken,
        },
      }
    }
  }

  const emptyProps = getEmptyProps()

  const shouldRenderAnalytics = !!activeIntegration && !emptyProps && !isLoading

  return (
    <>
      <Helmet>
        <title>Analytics</title>
        <link id="favicon" rel="icon" href={`${REACT_APP_PUBLIC_URL}/blazeFavicon.ico`} type="image/x-icon" />
      </Helmet>
      <MetaTags url={ANALYTICS_PATH} title="Analytics" description="Analytics" />
      <AnalyticsProvider>
        <div className={Styles.root}>
          <IntegrationsBanner />
          <Header />
          <div className={Styles.content}>
            {isLoading && <LoadingSkeleton />}
            {emptyProps && <Empty {...emptyProps} />}
            {shouldRenderAnalytics && activeIntegration?.provider === 'instagram' && <InstagramAnalytics />}
            {shouldRenderAnalytics && activeIntegration?.provider === 'facebook' && <FacebookAnalytics />}
            {shouldRenderAnalytics && activeIntegration?.provider === 'linkedin' && <LinkedInAnalytics />}
            {shouldRenderAnalytics && activeIntegration?.provider === 'x' && <XAnalytics />}
          </div>
        </div>
      </AnalyticsProvider>
    </>
  )
}

export default Analytics
